import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/Button";
import { useAuth } from "../utils/auth";
import { useUser } from "../contexts/UserContext";
import { deleteAccount, generateAccountDeleteOTP } from "../utils/api";
import { UIUtils } from "../utils/uiUtils";
import { useBottomNavBar } from "../contexts/BottomNavBarContext";

const AccountDeletion: React.FC = () => {
  const { user } = useAuth();
  const { user: userData } = useUser();
  const navigate = useNavigate();
  const { setIsVisible } = useBottomNavBar();
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [reason, setReason] = useState("");
  
  // OTP related states
  const [showOtpForm, setShowOtpForm] = useState(false);
  const [otp, setOtp] = useState("");
  const [referenceId, setReferenceId] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  useEffect(() => {
    setIsVisible(true);
    return () => setIsVisible(false);
  }, [setIsVisible]);

  // Redirect to login if not logged in
  useEffect(() => {
    if (!user) {
      navigate("/login?redirectTo=/account-deletion");
    }
  }, [user, navigate]);

  // Start countdown timer for OTP resend
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendCountdown > 0) {
      timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1);
      }, 1000);
    } else {
      setResendDisabled(false);
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [resendCountdown]);

  const handleRequestOTP = async () => {
    if (!isConfirmed) {
      setSubmitError("Please confirm that you want to delete your account");
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError("");
    setOtpError("");
    
    try {
      // Request OTP for account deletion
      const response = await generateAccountDeleteOTP({
        email: userData?.email || ""
      });
      
      // Check if the response contains an error status
      if (response.data.status === "error") {
        setSubmitError("Failed to send OTP. Please try again.");
        return;
      }
      
      if (response.data.status === "success") {
        setReferenceId(response.data.reference_id);
        setOtpSent(true);
        setShowOtpForm(true);
        
        // Start countdown for resend button
        setResendDisabled(true);
        setResendCountdown(60); // 60 seconds countdown
        
        // Track OTP request
        UIUtils.trackButtonClick("Account Deletion OTP Request");
      } else {
        setSubmitError("Failed to send OTP. Please try again.");
      }
    } catch (error: any) {
      setSubmitError("There was an error sending the verification code. Please try again later.");
      UIUtils.trackException("Account deletion OTP request", error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleResendOTP = async () => {
    if (resendDisabled) return;
    
    setIsSubmitting(true);
    setOtpError("");
    
    try {
      // Request new OTP
      const response = await generateAccountDeleteOTP({
        email: userData?.email || ""
      });
      
      // Check if the response contains an error status
      if (response.data.status === "error") {
        setOtpError("Failed to resend verification code. Please try again.");
        return;
      }
      
      if (response.data.status === "success") {
        setReferenceId(response.data.reference_id);
        
        // Start countdown for resend button
        setResendDisabled(true);
        setResendCountdown(60); // 60 seconds countdown
        
        // Show success message
        setOtpError("Verification code resent successfully.");
        
        // Track OTP resend
        UIUtils.trackButtonClick("Account Deletion OTP Resend");
      } else {
        setOtpError("Failed to resend verification code. Please try again.");
      }
    } catch (error: any) {
      setOtpError("There was an error resending the verification code. Please try again later.");
      UIUtils.trackException("Account deletion OTP resend", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isConfirmed) {
      setSubmitError("Please confirm that you want to delete your account");
      return;
    }
    
    // If OTP hasn't been requested yet, request it first
    if (!otpSent) {
      await handleRequestOTP();
      return;
    }
    
    // Validate OTP
    if (!otp) {
      setOtpError("Please enter the verification code");
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError("");
    setOtpError("");
    
    try {
      // Prepare account deletion data with OTP
      const deleteData = {
        whoYouAre: "user",
        helpWith: "delete_account",
        email: userData?.email || "",
        otp: otp,
        reference_id: referenceId,
        reason: reason || ""
      };
      
      // Submit the account deletion request to the backend
      const response = await deleteAccount(deleteData);
      
      // Check if the response contains an error status
      if (response.data.status === "error") {
        setSubmitError("Failed to delete account. Please try again.");
        return;
      }
      
      if (response.data.status === "success") {
        // Track account deletion
        UIUtils.trackButtonClick("Account Deletion Confirm");
        
        // Show success message
        setSubmitSuccess(true);
        
        // Clear auth data after a short delay to allow the user to see the success message
        // Get the API instance and clear the token
        const api = (window as any).api || {};
        if (api.clearToken) {
          api.clearToken();
        } else {
          // Fallback: clear localStorage and sessionStorage
          localStorage.removeItem("afforda_auth_token");
          localStorage.removeItem("afforda_user_data");
          sessionStorage.clear();
        }
        setTimeout(() => {
          // Redirect to home page after 5 seconds
          window.location.replace("/");
        }, 5000);
      } else {
        setSubmitError("Failed to delete account. Please try again.");
      }
    } catch (error: any) {
      console.error("Error deleting account:", error);
      
      // Check if it's an OTP verification error
      if (error.response?.data?.verification_status) {
        const status = error.response.data.verification_status;
        if (status === "EXPIRED") {
          setOtpError("The verification code has expired. Please request a new one.");
        } else if (status === "ATTEMPTS_EXHAUSTED") {
          setOtpError("Too many incorrect attempts. Please request a new verification code.");
        } else if (status === "INCORRECT_OTP") {
          setOtpError("Incorrect verification code. Please try again.");
        } else {
          setOtpError("Invalid verification code. Please try again.");
        }
      } else {
        setSubmitError("There was an error processing your request. Please try again later.");
      }
      
      UIUtils.trackException("Account deletion", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      {/* Breadcrumb */}
      <nav className="flex mb-6 text-sm">
        <Link to="/" className="text-gray-500 hover:text-orange-500">Home</Link>
        <span className="mx-2 text-gray-400">/</span>
        <Link to="/preferences" className="text-gray-500 hover:text-orange-500">Profile Settings</Link>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-gray-900">Account Deletion</span>
      </nav>
      
      {/* Page heading */}
      <h1 className="text-3xl font-bold mb-8">Delete Account</h1>
      
      {submitSuccess ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-green-800 mb-2">
            Account Deleted
          </h2>
          <p className="text-green-700 mb-4">
            Your account has been successfully deleted.
          </p>
          <p className="text-green-700 mb-4">
            Thank you for using Afforda. You're welcome back anytime.
          </p>
          <Button 
            text="Return to Homepage" 
            onClick={() => navigate("/")}
            backgroundColor="bg-green-600"
            className="hover:bg-green-700"
          />
        </div>
      ) : (
        <>
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
            <p className="text-red-700 mb-4">
              We're sorry to see you go.
            </p>
            <p className="text-red-700 mb-2">
              Before you confirm:
            </p>
            <ul className="list-disc pl-8 text-red-700 mb-4">
              <li className="mb-1">All your data will be permanently deleted</li>
              <li className="mb-1">You'll lose access to your personalised recommendations and discount deals.</li>
              <li className="mb-1">To use Afforda again, you'll need to create a new account</li>
            </ul>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email - show as disabled */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={userData?.email || ""}
                disabled
                className="w-full px-4 py-2 border rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed border-gray-300"
              />
            </div>
            
            {/* Reason dropdown */}
            <div>
              <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-1">
                Why are you leaving? (optional)
              </label>
              <select
                id="reason"
                name="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg border-gray-300 focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="">Select a reason</option>
                <option value="Found a better alternative">Found a better alternative</option>
                <option value="No longer need financial guidance">No longer need financial guidance</option>
                <option value="Prefer not to share personal data">Prefer not to share personal data</option>
                <option value="Service doesn't meet my needs">Service doesn't meet my needs</option>
                <option value="Other">Other</option>
              </select>
            </div>
            
            {/* OTP input field - shown after OTP is sent */}
            {showOtpForm && (
              <div>
                <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-1">
                  Verification Code
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    id="otp"
                    name="otp"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="Enter the 6-digit code sent to your email"
                    className="w-full px-4 py-2 border rounded-lg border-gray-300 focus:ring-orange-500 focus:border-orange-500"
                    maxLength={6}
                  />
                </div>
                {otpError && (
                  <p className={`mt-2 text-sm ${otpError.includes("resent successfully") ? "text-green-600" : "text-red-600"}`}>
                    {otpError}
                  </p>
                )}
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={resendDisabled}
                    className={`text-sm text-orange-600 hover:text-orange-500 ${
                      resendDisabled ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    {resendDisabled
                      ? `Resend code in ${resendCountdown}s`
                      : "Resend verification code"}
                  </button>
                </div>
              </div>
            )}
            
            {/* Confirmation checkbox */}
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="confirm"
                  name="confirm"
                  type="checkbox"
                  checked={isConfirmed}
                  onChange={(e) => setIsConfirmed(e.target.checked)}
                  className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="confirm" className="font-medium text-gray-700">
                  I confirm that I want to permanently delete my account and all associated data
                </label>
              </div>
            </div>
            
            {/* Submit button */}
            <div className="flex space-x-4">
              <Button
                text="Cancel"
                onClick={() => navigate("/preferences")}
                backgroundColor="bg-gray-500"
                className="hover:bg-gray-600"
              />
              <Button
                text={otpSent ? "Delete My Account" : "Continue"}
                type="submit"
                onClick={() => {}}
                isLoading={isSubmitting}
                loadingText="Processing..."
                disabled={isSubmitting || !isConfirmed}
                backgroundColor="bg-red-600"
                className="hover:bg-red-700"
              />
            </div>
            
            {/* Error message */}
            {submitError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
                <p className="text-red-700">{submitError}</p>
              </div>
            )}
          </form>
        </>
      )}
    </div>
  );
};

export default AccountDeletion;
